import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { boardApi } from '@/features/project-board/api';
import { EpicMembership } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';
import type { BoardIndex, BoardSource, BoardThroughput, Epic, Incident, Issue, Task } from '@/features/project-board/types';

/**
 * 보드 섹션 데이터 + 필터 상태.
 *
 * 화면 4개가 같은 인덱스를 본다(서버도 한 번의 스캔으로 준다) — 화면을 옮길 때 다시 불러오면
 * 화면끼리 다른 시점을 보게 된다. 필터는 화면을 넘어가도 유지한다.
 */
export interface BoardFilters {
  /**
   * 볼 하네스 저장소(`market`·`uncle`). 빈 문자열이면 전체.
   *
   * 다른 필터와 달리 이건 **데이터 범위**다 — 서버가 저장소를 섞어 한 벌로 주고, 여기서 좁힌 결과가
   * `index`로 나간다. 그래서 대시보드처럼 `tasks`/`issues`를 안 쓰는 화면도 자동으로 따라온다.
   */
  source: string;
  query: string;
  owner: string;
  epic: string;
  /** 에픽 화면: 현재 단계로 좁힌다. 빈 문자열이면 전체(`EPIC_STAGE_COLUMNS`의 key). */
  stage: string;
  /** task 화면: 제외(dropped) 숨김. */
  hideDropped: boolean;
  /** 이슈 화면: 완료 숨김. task의 '제외'와 의미가 달라 플래그를 나눈다 —
   *  하나로 묶었더니 완료 이슈 88건이 기본으로 사라져 있었다. */
  hideDoneIssues: boolean;
  /** 인시던트 화면: 완료 숨김. 이슈와 같은 이유로 별도 플래그 — 공용으로 묶지 않는다. */
  hideDoneIncidents: boolean;
  /** 에픽 화면: 100% 완료 에픽 숨김. task의 '제외'·이슈의 '완료'와 축이 달라 플래그를 나눈다. */
  hideDoneEpics: boolean;
  /** 선행이 안 끝나 막힌 task만. 대시보드의 "막힌 task"에서 넘어올 때 켜진다. */
  onlyBlocked: boolean;
  swimlane: boolean;
}

interface BoardContextValue {
  /** 고른 저장소로 좁힌 색인. 저장소를 고르지 않았으면 받은 그대로다. */
  index: BoardIndex | null;
  /** 완료일별 처리량(서버 집계). 인덱스와 같은 시점에 받고, 고른 저장소로 같이 좁힌다. */
  throughput: BoardThroughput | null;
  /** 색인된 저장소 목록 — 좁히기 전 값이라 저장소를 골라도 선택지가 사라지지 않는다. */
  sources: BoardSource[];
  error: string | null;
  loading: boolean;
  reload: () => void;
  filters: BoardFilters;
  setFilters: (patch: Partial<BoardFilters>) => void;
  /** 필터를 적용한 에픽. `index.epics`는 필터 전 목록이라 후보 드롭다운이 줄어들지 않는다. */
  epics: Epic[];
  tasks: Task[];
  issues: Issue[];
  incidents: Incident[];
}

const BoardContext = createContext<BoardContextValue | null>(null);

const INITIAL_FILTERS: BoardFilters = {
  // 기본은 전체 — 저장소 하나를 기본으로 박으면 다른 저장소가 있다는 것 자체가 안 보인다.
  source: '',
  query: '',
  owner: '',
  epic: '',
  stage: '',
  hideDropped: true,
  // 이슈는 완료까지 보이는 게 기본 — 파이프라인 전체(리뷰·E2E 완료 포함)를 보는 화면이다.
  hideDoneIssues: false,
  // 인시던트도 이슈와 같은 이유로 완료까지 기본 노출.
  hideDoneIncidents: false,
  // 에픽도 완료까지 보이는 게 기본 — 끝난 일이 안 보이면 분기 성과가 화면에서 사라진다.
  hideDoneEpics: false,
  onlyBlocked: false,
  swimlane: false,
};

export function BoardProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [indexed, setIndexed] = useState<BoardIndex | null>(null);
  const [throughput, setThroughput] = useState<BoardThroughput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilterState] = useState<BoardFilters>(INITIAL_FILTERS);
  useSeedFiltersFromUrl(setFilterState);

  const reload = useCallback(() => {
    setLoading(true);
    // 둘을 함께 받는다 — 따로 부르면 새로고침 사이에 색인이 돌아 대시보드 수치와 카드 수가 어긋난다.
    Promise.all([boardApi.index(), boardApi.throughput()])
      .then(([nextIndex, nextThroughput]) => {
        setIndexed(nextIndex);
        setThroughput(nextThroughput);
        setError(null);
      })
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const setFilters = useCallback((patch: Partial<BoardFilters>) => {
    setFilterState((current) => ({ ...current, ...patch }));
  }, []);

  const index = useMemo(() => scopeToSource(indexed, filters.source), [indexed, filters.source]);
  const scopedThroughput = useMemo(
    () =>
      throughput === null || filters.source === ''
        ? throughput
        : { ...throughput, days: throughput.days.filter((day) => day.source === filters.source) },
    [throughput, filters.source],
  );
  const epics = useMemo(
    () => filterEpics(index?.epics ?? [], index?.tasks ?? [], filters),
    [index, filters],
  );
  const tasks = useMemo(
    () => filterTasks(index?.tasks ?? [], index?.epics ?? [], filters),
    [index, filters],
  );
  const issues = useMemo(() => filterIssues(index?.issues ?? [], filters), [index, filters]);
  const incidents = useMemo(() => filterIncidents(index?.incidents ?? [], filters), [index, filters]);

  const value: BoardContextValue = {
    index,
    throughput: scopedThroughput,
    sources: indexed?.sources ?? [],
    error,
    loading,
    reload,
    filters,
    setFilters,
    epics,
    tasks,
    issues,
    incidents,
  };
  return <BoardContext.Provider value={value}>{children}</BoardContext.Provider>;
}

/**
 * 주소로 들어온 범위를 필터에 심는다 — 개발요청 화면에서 "연결된 스펙 보기"로 넘어오는 경로다.
 *
 * 심은 뒤 쿼리에서 지운다. 남겨 두면 (1) 이 효과가 다시 돌아 사용자가 방금 바꾼 필터를 되돌리고,
 * (2) 주소가 필터의 정본인 척하게 된다 — 보드 필터의 정본은 계속 이 컨텍스트다.
 *
 * 섹션 안에서 화면을 옮겨도 이 provider는 그대로 살아 있어(같은 위치·같은 타입) 마운트 시점에
 * 한 번 읽는 방식으로는 잡히지 않는다. 그래서 쿼리 변화를 보고 반응한다.
 */
function useSeedFiltersFromUrl(apply: React.Dispatch<React.SetStateAction<BoardFilters>>): void {
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const source = params.get('source');
    const epic = params.get('epic');
    if (source === null && epic === null) return;

    apply((current) => ({
      ...current,
      ...(source === null ? {} : { source }),
      ...(epic === null ? {} : { epic }),
    }));

    const rest = new URLSearchParams(params);
    rest.delete('source');
    rest.delete('epic');
    setParams(rest, { replace: true });
  }, [params, setParams, apply]);
}

export function useBoard(): BoardContextValue {
  const value = useContext(BoardContext);
  if (!value) throw new Error('useBoard는 BoardProvider 안에서만 쓴다');
  return value;
}

export class BoardSearch {
  static matches(haystack: string, query: string): boolean {
    return query === '' || haystack.toLowerCase().includes(query.toLowerCase());
  }
}

/**
 * 색인을 저장소 하나로 좁힌다.
 *
 * 목록만 걸러내는 게 아니라 **필터 후보와 요약 수치도 다시 만든다** — 담당자 목록에 다른 저장소 사람이
 * 남아 있으면 고를 수는 있는데 결과가 0건이 된다.
 */
function scopeToSource(index: BoardIndex | null, source: string): BoardIndex | null {
  if (!index) return null;
  if (source === '') return { ...index, facets: { ...index.facets, owners: ownerFacetOf(index) } };

  const epics = index.epics.filter((epic) => epic.source === source);
  const tasks = index.tasks.filter((task) => task.source === source);
  const issues = index.issues.filter((issue) => issue.source === source);
  const incidents = index.incidents.filter((incident) => incident.source === source);

  return {
    ...index,
    epics,
    tasks,
    issues,
    incidents,
    facets: {
      owners: ownerFacetOf({ ...index, epics, tasks, issues, incidents }),
      repos: unique(tasks.map((task) => task.repo)),
    },
    stats: {
      epics: epics.length,
      tasks: tasks.length,
      issues: issues.length,
      openIssues: issues.filter((issue) => issue.status !== 'done').length,
      incidents: incidents.length,
      openIncidents: incidents.filter((incident) => incident.status !== 'done').length,
    },
  };
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort();
}

/**
 * 담당자 후보 — 서버가 준 목록(문서 셀 값 그대로)을 쓰지 않고 여기서 명부 기준으로 다시 만든다.
 *
 * 셀 값을 그대로 실으면 AI 에이전트·역할·표기 중복까지 후보로 올라온다(실측 34개). `assigneeOf`와
 * **같은 판정**을 거친 이름만 남겨야 고른 담당자가 결과 0건이 되지 않는다.
 *
 * 이슈·인시던트의 `handledBy`(커밋 author)도 같은 명부(`memberOf`)로 걸러 후보에 넣는다 — task는 없고
 * 이슈·인시던트만 처리한 사람은 task 쪽 후보에 안 잡혀 필터에서 고를 수 없었다.
 */
function ownerFacetOf(index: Pick<BoardIndex, 'epics' | 'tasks' | 'issues' | 'incidents'>): string[] {
  return unique([
    ...index.tasks.map((task) => OwnerRoster.assigneeOf(task, EpicMembership.epicOf(index.epics, task))),
    ...index.issues.map((issue) => OwnerRoster.memberOf(issue.handledBy)),
    ...index.incidents.map((incident) => OwnerRoster.memberOf(incident.handledBy)),
  ]);
}

/**
 * 담당자 필터는 에픽에도 걸린다 — 에픽 문서에 담당자 칸이 없어 **화면 '담당' 열과 같은 기준**을 쓴다:
 * 기획 문서를 만든 담당자거나 그 에픽 task를 든 사람이면 그 사람의 에픽이다. 기준이 어긋나면 얼굴이
 * 떠 있는 에픽이 필터에서 빠진다.
 *
 * 거르는 건 목록뿐이다 — 카드의 진행률·카운트는 에픽 전체 기준 그대로다. 담당자를 골랐다고 그 사람
 * 몫만 다시 세면 "이 에픽이 얼마나 끝났나"라는 수치의 뜻이 바뀐다.
 */
function filterEpics(epics: Epic[], tasks: Task[], filters: BoardFilters): Epic[] {
  return epics.filter((epic) => {
    if (filters.hideDoneEpics && epic.donePct >= 100) return false;
    if (filters.stage && epic.stage !== filters.stage) return false;
    if (filters.owner && !OwnerRoster.isEpicPerson(epic, tasks, filters.owner)) {
      return false;
    }
    return true;
  });
}

function filterTasks(tasks: Task[], epics: Epic[], filters: BoardFilters): Task[] {
  return tasks.filter((task) => {
    if (filters.epic && task.epicSlug !== filters.epic) return false;
    if (filters.owner && OwnerRoster.assigneeOf(task, EpicMembership.epicOf(epics, task)) !== filters.owner) return false;
    if (filters.hideDropped && task.status === 'dropped') return false;
    if (filters.onlyBlocked && task.blockedBy.length === 0) return false;
    return BoardSearch.matches(
      [task.taskId, task.title, task.repo, task.owner, task.epicSlug, task.group, task.scope].join(' '),
      filters.query,
    );
  });
}

function filterIssues(issues: Issue[], filters: BoardFilters): Issue[] {
  return issues.filter((issue) => {
    if (filters.hideDoneIssues && issue.status === 'done') return false;
    if (filters.owner && OwnerRoster.memberOf(issue.handledBy) !== filters.owner) return false;
    return BoardSearch.matches(
      [issue.id, issue.title, issue.summary, issue.repos.join(' '), issue.handledBy].join(' '),
      filters.query,
    );
  });
}

function filterIncidents(incidents: Incident[], filters: BoardFilters): Incident[] {
  return incidents.filter((incident) => {
    if (filters.hideDoneIncidents && incident.status === 'done') return false;
    if (filters.owner && OwnerRoster.memberOf(incident.handledBy) !== filters.owner) return false;
    return BoardSearch.matches(
      [incident.id, incident.title, incident.summary, incident.repos.join(' '), incident.handledBy].join(' '),
      filters.query,
    );
  });
}
